import { galText } from "./accounts";
import type { PlanStop, PlanTruck } from "./api";

export interface TruckLoad {
  gal: number;
  share: number;
}

/** The gallons that a stop puts on the truck: none for an account that was emptied, the float limit when it is lower. */
export function stopGal(stop: Pick<PlanStop, "kind" | "expected_gal" | "emptied_on" | "float_now">): number {
  if (stop.kind !== "stop" || stop.emptied_on || stop.expected_gal === null) return 0;
  const below = stop.float_now && !stop.float_now.full ? stop.float_now.max_gal : null;
  return below == null ? stop.expected_gal : Math.min(stop.expected_gal, below);
}

/** The truck load after each stop (rule U-27): a dump empties the truck. All null without a truck size. */
export function truckLoads(truck: Pick<PlanTruck, "capacity" | "stops">): (TruckLoad | null)[] {
  const capacity = truck.capacity;
  let gal = 0;
  return truck.stops.map((stop) => {
    if (stop.kind === "dump") {
      gal = 0;
      return null;
    }
    gal += stopGal(stop);
    return capacity ? { gal, share: gal / capacity } : null;
  });
}

/** "Truck after this stop: about 540 gal of 750 gal (72%)". */
export function truckLoadText(load: TruckLoad, capacity: number): string {
  const head = `Truck after this stop: about ${galText(Math.round(load.gal))} of ${galText(capacity)}`;
  return load.share > 1 ? `${head}, more than the truck holds` : `${head} (${Math.round(load.share * 100)}%)`;
}

const BOX_H = 11;

/** The truck symbol: the box fills from the bottom in one colour; over the truck size it is full and striped. */
export function truckSvg(share: number): string {
  const over = share > 1;
  const h = (BOX_H * Math.min(Math.max(share, 0), 1)).toFixed(1);
  const stripes = over ? '<path class="truck-stripes" d="M4 13l6-11M9 13l6-11M14 13l6-11" />' : "";
  return (
    '<svg viewBox="-1 -1 34 21" width="44" height="27" aria-hidden="true">' +
    '<rect class="truck-plate" x="-1" y="-1" width="34" height="21" rx="4" />' +
    `<rect class="truck-fill" x="2" y="${(2 + BOX_H - Number(h)).toFixed(1)}" width="18" height="${h}" />` +
    stripes +
    '<rect class="truck-line" x="2" y="2" width="18" height="11" rx="1" />' +
    '<path class="truck-line" d="M21 5h5l4 4v4h-9z" />' +
    '<circle class="truck-wheel" cx="7" cy="15.5" r="2.4" /><circle class="truck-wheel" cx="25" cy="15.5" r="2.4" />' +
    "</svg>"
  );
}
